import React from "react";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";

export interface AttachUtilityServiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  unitName?: string;
  consumerUtilityServices: string[];
  isLoading?: boolean;
}

export const AttachUtilityServiceDialog: React.FC<
  AttachUtilityServiceDialogProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  unitName = "this unit",
  consumerUtilityServices,
  isLoading = false,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="max-w-md"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold text-gray-900">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
            Attach Utility Services to Unit
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          <p className="text-gray-600 leading-relaxed">
            The selected unit{" "}
            <span className="font-bold text-gray-900">{unitName}</span> does not
            have any utility services configured.
          </p>

          <div className="p-3.5 bg-blue-50/70 border border-blue-200 rounded-xl space-y-2">
            <span className="text-xs font-bold text-blue-950 uppercase tracking-wide block">
              Consumer Utility Services
            </span>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {consumerUtilityServices && consumerUtilityServices.length > 0 ? (
                consumerUtilityServices.map((service: string, idx: number) => (
                  <span
                    key={idx}
                    className="px-2.5 py-1 text-xs font-semibold rounded-md bg-white border border-blue-200 text-blue-700 shadow-2xs"
                  >
                    {service}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-500 italic">
                  No consumer utility services available
                </span>
              )}
            </div>
          </div>

          <p className="text-gray-800 font-medium">
            Do you want to attach consumer utility service to the unit?
          </p>
          <p className="text-xs text-gray-500">
            If no, please update the unit and add utility service to the unit.
          </p>
        </div>

        <DialogFooter className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            No
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={
              isLoading ||
              !consumerUtilityServices ||
              consumerUtilityServices.length === 0
            }
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
          >
            {isLoading ? "Attaching..." : "Yes, Attach"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttachUtilityServiceDialog;
